import { DropDownButton, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import React, { useContext, useMemo } from 'react';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import { Label } from '@progress/kendo-react-labels';
import {
  useSearchContext,
  useViewsContext,
  useGlobalizationContext,
  useGetView,
  useDocumentsContext,
  useDocumentFormContext,
  useNotificationContext,
  useCurrentUserContext,
  useGetCtsWithCreatePermission,
  useLockDocuments,
  useUnlockDocuments,
  useDeleteDocuments,
  useGetUserPermissions,
  useGetAllFields,
  useTranslation,
  ServicesContext
} from '@cs-cms/state-management';
import { CPS_FIELD_TYPE, GetDocumentMetadataRequest } from '@cs-cms/types';
import { useCpsDialog } from '../../../common/useCpsDialog';
import { FilterActionsContextMenu } from './FilterActionsContextMenu';
import {
  ActionItemId,
  ClickedCellMetadata,
  ConfirmDialogType,
  DropDownButtonItem,
  TooManyRequestsTimer
} from './FilterActions.types';
import { buildCellValueFilterUpdate, getVisibleGlobalFilters } from './applyCellValueFilter';
import { buildContextMenuItems } from './buildContextMenuItems';
import { buildFilterActionItems } from './buildFilterActionItems';
import { FilterActionHandlerContext, handleFilterActionClick } from './filterActionHandlers';
import { FilterActionsExportDialog } from './FilterActionsExportDialog';
import { useFilterActionsTooManyRequestsTimer } from './useFilterActionsTooManyRequestsTimer';
import { useDocumentPreviewContext } from '../../documentPreview';

interface FilterActionsProps {
  onMountActions?: (actions: FilterActionsHandle) => void;
}

export interface FilterActionsHandle {
  openContextMenu: (x: number, y: number, cell?: ClickedCellMetadata) => void;
}

export const FilterActions: React.FC<FilterActionsProps> = ({ onMountActions }) => {
  const {
    data,
    queryRequest,
    setQueryRequest,
    compositeFilterDescriptor,
    setCompositeFilterDescriptor,
    setRequestedGlobalDateFieldName,
    handleTriggerExportToCsv
  } = useSearchContext();
  const { selectedView } = useViewsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { data: currentView } = useGetView(selectedView, selectedLocale);
  const { selectedDocuments, downloadFiles } = useDocumentsContext();
  const [dialogVisible, setDialogVisible] = React.useState<ConfirmDialogType | undefined>(undefined);
  const { openNewDocumentForm, openEditDocumentForm } = useDocumentFormContext();
  const { openDocumentPreview } = useDocumentPreviewContext();
  const [tooManyRequestsTimer, setTooManyRequestsTimer] = React.useState<TooManyRequestsTimer | undefined>(undefined);
  const { showToast } = useNotificationContext();
  const { showDialog, DialogComponent } = useCpsDialog();
  const { selectedIndex } = useCurrentUserContext();
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);
  const { data: ctsWithCreatePermissions } = useGetCtsWithCreatePermission(selectedIndex?.id);
  const { mutate: mutateLockDocuments } = useLockDocuments();
  const { mutate: mutateUnlockDocuments } = useUnlockDocuments();
  const { mutate: mutateDeleteDocuments } = useDeleteDocuments();
  const { data: permissions } = useGetUserPermissions();
  const { translate } = useTranslation(selectedLocale);
  const [contextMenu, setContextMenu] = React.useState<{
    x: number;
    y: number;
    cell?: ClickedCellMetadata;
  } | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const servicesContext = useContext(ServicesContext);

  useFilterActionsTooManyRequestsTimer({
    tooManyRequestsTimer,
    setTooManyRequestsTimer,
    dialogVisible,
    setDialogVisible
  });

  const actionsRef = React.useRef({
    openContextMenu: (x: number, y: number, cell?: ClickedCellMetadata) => {
      setContextMenu({ x, y, cell });
    }
  });

  const handleContainerRef = (node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node && onMountActions) {
      onMountActions(actionsRef.current);
    }
  };

  const selectedDocArray = useMemo(() => selectedDocuments ?? [], [selectedDocuments]);
  const isAddHidden = !ctsWithCreatePermissions || ctsWithCreatePermissions.length === 0;

  const getDocumentMetadata = React.useCallback(
    (dto: GetDocumentMetadataRequest) => {
      if (!servicesContext.documentsService) {
        return Promise.reject(new Error('Documents service is not available'));
      }
      return servicesContext.documentsService.getDocumentMetadata(dto);
    },
    [servicesContext.documentsService]
  );

  const items = useMemo(
    () =>
      buildFilterActionItems({
        translate,
        isAddHidden,
        canExportCsv: Boolean(permissions?.csvExport),
        selectedDocuments: selectedDocArray,
        ctsWithCreatePermissions
      }),
    [translate, isAddHidden, permissions?.csvExport, selectedDocArray, ctsWithCreatePermissions]
  );

  const handlerContext = useMemo((): FilterActionHandlerContext => {
    return {
      translate,
      showDialog,
      showToast,
      selectedDocuments,
      selectedIndex,
      downloadFiles,
      mutateLockDocuments,
      mutateUnlockDocuments,
      mutateDeleteDocuments,
      openNewDocumentForm,
      openEditDocumentForm,
      openDocumentPreview,
      getDocumentMetadata,
      exportContext: {
        dataTotalCount: data?.totalCount,
        queryRequest,
        currentViewFieldNames: currentView?.fields.map(column => column.name),
        selectedLocale,
        selectedView,
        handleTriggerExportToCsv,
        tooManyRequestsTimer,
        setTooManyRequestsTimer,
        setDialogVisible
      }
    };
  }, [
    translate,
    showDialog,
    showToast,
    selectedDocuments,
    selectedIndex,
    downloadFiles,
    mutateLockDocuments,
    mutateUnlockDocuments,
    mutateDeleteDocuments,
    openNewDocumentForm,
    openEditDocumentForm,
    openDocumentPreview,
    getDocumentMetadata,
    data?.totalCount,
    queryRequest,
    currentView?.fields,
    selectedLocale,
    selectedView,
    handleTriggerExportToCsv,
    tooManyRequestsTimer
  ]);

  const handleActionClick = (item: DropDownButtonItem): void => {
    if (item.isDisabled || item.id === ActionItemId.GROUP) {
      return;
    }
    handleFilterActionClick(item.id, handlerContext, selectedDocArray);
  };

  const itemClicked = (event: DropDownButtonItemClickEvent): void => {
    handleActionClick(event.item);
  };

  const handleItemRender = (li: { item: DropDownButtonItem; itemIndex: number }) => {
    return (
      <div className={li.item.className || `filterActionButtonOption`}>
        {li.item.svgIcon && <SvgIcon icon={li.item.svgIcon} />}
        <Label key={li.item.id}>{li.item.text}</Label>
      </div>
    );
  };

  const canFilterByCell = useMemo((): boolean => {
    const fieldName = contextMenu?.cell?.fieldName;
    if (!fieldName) {
      return false;
    }

    const column = currentView?.fields.find(field => field.name === fieldName);
    if (column?.filterable) {
      return true;
    }

    return getVisibleGlobalFilters(currentView?.globalFilters, allFields).some(filter => filter.name === fieldName);
  }, [allFields, contextMenu?.cell?.fieldName, currentView?.fields, currentView?.globalFilters]);

  const contextMenuItems = useMemo(
    () =>
      buildContextMenuItems({
        items,
        translate,
        clickedCell: contextMenu?.cell,
        ctsWithCreatePermissions,
        canFilterByCell
      }),
    [items, translate, contextMenu?.cell, ctsWithCreatePermissions, canFilterByCell]
  );

  const applyClickedCellFilter = (clickedCell: ClickedCellMetadata | undefined): void => {
    if (!clickedCell) {
      return;
    }

    const visibleGlobalFilters = getVisibleGlobalFilters(currentView?.globalFilters, allFields);
    const globalFilter = visibleGlobalFilters.find(filter => filter.name === clickedCell.fieldName);
    const groupedDateFieldNames = visibleGlobalFilters
      .filter(filter => filter.type === CPS_FIELD_TYPE.DATE || filter.type === CPS_FIELD_TYPE.DATETIME)
      .map(filter => filter.name);
    const update = buildCellValueFilterUpdate({
      fieldName: clickedCell.fieldName,
      value: clickedCell.value,
      globalFilter,
      hasFacets: data?.facets?.[clickedCell.fieldName] !== undefined,
      groupedDateFieldNames,
      queryRequest,
      compositeFilterDescriptor
    });

    if (update.queryRequest) {
      setQueryRequest(update.queryRequest);
    }
    if (update.compositeFilterDescriptor && setCompositeFilterDescriptor) {
      setCompositeFilterDescriptor(update.compositeFilterDescriptor);
    }
    if (update.requestedGlobalDateFieldName) {
      setRequestedGlobalDateFieldName(update.requestedGlobalDateFieldName);
    }
  };

  const handleContextMenuItemClick = (item: DropDownButtonItem): void => {
    const clickedCell = contextMenu?.cell;
    setContextMenu(null);
    if (item.id === ActionItemId.CREATE_FROM_CELL) {
      if (!clickedCell) {
        return;
      }
      openNewDocumentForm({ [clickedCell.fieldName]: clickedCell.value }, clickedCell.contentType);
      return;
    }
    if (item.id === ActionItemId.FILTER_BY_CELL) {
      applyClickedCellFilter(clickedCell);
      return;
    }
    handleActionClick(item);
  };

  return (
    <div>
      <div ref={handleContainerRef}>
        <DropDownButton
          endIcon={<SvgIcon icon={chevronDownIcon} />}
          text={translate('custom.toolbar.actions.title')}
          items={items}
          itemRender={handleItemRender}
          size={'medium'}
          themeColor={'base'}
          fillMode="solid"
          onItemClick={itemClicked}
          popupSettings={{
            popupClass: 'filterActionButtonPopup'
          }}
        />
      </div>

      <FilterActionsContextMenu
        position={contextMenu === null ? null : { x: contextMenu.x, y: contextMenu.y }}
        items={contextMenuItems}
        onItemClick={handleContextMenuItemClick}
        onClose={() => setContextMenu(null)}
      />

      {dialogVisible && (
        <FilterActionsExportDialog
          dialogVisible={dialogVisible}
          tooManyRequestsTimer={tooManyRequestsTimer}
          translate={translate}
          onConfirm={() => setDialogVisible(undefined)}
        />
      )}
      {DialogComponent}
    </div>
  );
};
